import { Injectable, computed, inject, signal } from '@angular/core';
import { CanActivateChildFn, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { ObraActivaService, ObraMinima } from '../services/obra-activa.service';
import { PermisosService } from '../services/permisos.service';
import { AvisoService } from './aviso.service';
import { CambiosPendientesService } from './cambios-pendientes.service';
import { NavegacionService } from './navegacion.service';
import {
  RAIZ_MENU, Tarjeta, botonPorId, conObra, departamentoPorId, destinoDe, obraDeConsulta, obraDeRuta, obrasDeTarjetas, obrasDelModulo, rutaDe,
  tarjetaDeRuta, usaObra,
} from './registro';

/** Módulos cuyo detalle (una cuadrilla, la producción de una cuadrilla, un documento) es de UNA obra: al cambiar de obra se vuelve a su lista. */
const DETALLE_DEPENDE_DE_LA_OBRA = new Set(['cuadrillas', 'produccion', 'documentos']);

/** Cómo se ve el selector de obra en la barra secundaria. */
export type ModoSelectorObra = 'oculto' | 'sin-obras' | 'chip' | 'lista';

/**
 * Lo que la consulta de la dirección conserva al cambiar de obra. La lista de Trabajadores de una obra lleva sus filtros en la dirección (cuadrilla, estado,
 * búsqueda, orden, página, ficha abierta): son de la obra que se deja (la cuadrilla 7 de la obra B no existe en la A) y se descartan; solo el tamaño de página es
 * de la persona y se queda. Las demás pantallas conservan su consulta tal cual (la pestaña de Bodega, `?obra=`…).
 */
export function alCambiarDeObra(url: string): string {
  if (!/\/rrhh\/trabajadores$/.test(rutaDe(url))) return url;
  const i = url.search(/[?#]/);
  const ruta = i < 0 ? url : url.slice(0, i);
  const tamano = new URLSearchParams(url.slice(i < 0 ? url.length : i).split('#')[0].replace(/^\?/, '')).get('pageSize');
  return tamano && /^\d+$/.test(tamano) ? `${ruta}?pageSize=${tamano}` : ruta;
}

/**
 * El selector de obra global (barra secundaria). Decide:
 *  - DÓNDE aparece: solo en pantallas de módulos que trabajan sobre una obra (registro: `obras === 'una'`) y en los menús que tengan al
 *    menos una tarjeta que la use;
 *  - QUÉ obras ofrece: solo aquellas donde el usuario tiene permiso EN ESE módulo (obrasDelModulo);
 *  - QUÉ pasa al cambiar: confirma si hay cambios sin guardar, deja la obra como activa (ObraActivaService.elegir: la fuente sigue siendo
 *    `sesion_obra_activa`), pone la misma pantalla con la obra nueva en la dirección (`replaceUrl`: cambiar de obra no suma pasos al historial)
 *    y vuelve a armar la pantalla para que cargue los datos de la obra nueva, sin recargar el navegador.
 */
@Injectable({ providedIn: 'root' })
export class ObraGlobalService {
  private router = inject(Router);
  private permisos = inject(PermisosService);
  private obraActiva = inject(ObraActivaService);
  private pendientes = inject(CambiosPendientesService);
  private avisos = inject(AvisoService);
  private nav = inject(NavegacionService);

  /** La dirección actual (sin consulta). */
  private readonly _url = signal(this.router.url);
  readonly url = this._url.asReadonly();

  /**
   * Sube cada vez que la PERSONA cambia de obra con el selector. MainLayout vuelve a armar la pantalla con esta llave: así todo lo que lee la
   * obra activa (la mayoría solo al abrirse) carga de nuevo, y una respuesta atrasada de la obra vieja cae en una pantalla ya destruida.
   */
  readonly llave = signal(0);

  readonly cambiando = signal(false);
  readonly error = signal('');

  constructor() {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this._url.set(e.urlAfterRedirects));
  }

  /** La tarjeta del registro de la pantalla actual, o null (inicio, menús, rutas fuera del registro). */
  readonly tarjeta = computed<Tarjeta | null>(() => tarjetaDeRuta(this._url()));

  /** Las tarjetas de la pantalla de menú actual (un botón, o los botones de un departamento); vacío si no es un menú. */
  private readonly tarjetasDelMenu = computed<Tarjeta[]>(() => {
    const ruta = rutaDe(this._url());
    if (!ruta.startsWith(RAIZ_MENU + '/')) return [];
    const [, dId, bId] = ruta.split('/').filter(Boolean);
    const dep = departamentoPorId(dId);
    if (!dep) return [];
    if (bId) return botonPorId(dep, bId)?.tarjetas ?? [];
    return dep.botones.flatMap(b => b.tarjetas);
  });

  /** ¿La pantalla actual trabaja sobre una obra (módulo o menú con tarjetas que la usan)? */
  readonly usaObraAqui = computed(() => {
    const t = this.tarjeta();
    if (t) return usaObra(t);
    return this.tarjetasDelMenu().some(usaObra);
  });

  /** Las obras que ofrece esta pantalla: donde el usuario tiene ese módulo (o, en un menú, alguno de los módulos que usan obra). */
  readonly obras = computed<ObraMinima[]>(() => {
    const todas = this.obraActiva.obras();
    const t = this.tarjeta();
    if (t) return usaObra(t) ? obrasDelModulo(t, todas, this.permisos) : [];
    return obrasDeTarjetas(this.tarjetasDelMenu(), todas, this.permisos, this.nav.concedidos());
  });

  readonly modo = computed<ModoSelectorObra>(() => {
    if (!this.usaObraAqui()) return 'oculto';
    // Antes de leer las obras no se sabe: no parpadea «sin obras».
    if (!this.obraActiva.cargado()) return 'oculto';
    const n = this.obras().length;
    return n === 0 ? 'sin-obras' : n === 1 ? 'chip' : 'lista';
  });

  /** La obra con la que se está trabajando: la activa si esta pantalla la ofrece; si no, la única o la primera que ofrece. */
  readonly actual = computed<ObraMinima | null>(() => {
    const ofrecidas = this.obras();
    const activa = ofrecidas.find(o => o.id === this.obraActiva.obraActivaId());
    return activa ?? ofrecidas[0] ?? null;
  });

  /**
   * Cambia de obra. Devuelve `false` si la persona decidió quedarse (cambios sin guardar) o si no se pudo.
   * Orden: confirmar → guardar la obra activa → misma pantalla con la obra nueva → volver a armar la pantalla.
   */
  async cambiar(obraId: number): Promise<boolean> {
    if (this.cambiando() || obraId === this.obraActiva.obraActivaId()) return false;
    this.error.set('');
    if (!(await this.pendientes.confirmarCambioDeObra())) return false;
    this.cambiando.set(true);
    try {
      try {
        await this.obraActiva.elegir(obraId);
      } catch {
        // El back rechaza obras fuera del alcance vigente: se resincroniza la lista y se dice.
        await this.obraActiva.cargar();
        this.error.set('No se pudo cambiar a esa obra. Inténtalo de nuevo en un momento.');
        this.avisos.mostrar(this.error(), 'alerta', 7000);
        return false;
      }
      const url = this._url();
      const nueva = this.urlConObra(url, obraId);
      if (nueva !== url) await this.router.navigateByUrl(nueva, { replaceUrl: true });
      this.llave.update(n => n + 1);
      return true;
    } finally {
      this.pendientes.terminarDescarte();
      this.cambiando.set(false);
    }
  }

  /**
   * La dirección de la misma pantalla con otra obra. Si lleva la obra, se cambia (conserva la pestaña de Bodega, la consulta…); si es el
   * DETALLE de algo propio de la obra anterior (una cuadrilla, la producción de una cuadrilla), ese detalle no existe en la nueva: vuelve a
   * la lista del módulo.
   */
  private urlConObra(url: string, obraId: number): string {
    const t = this.tarjeta();
    const enUrl = obraDeRuta(url);
    if (t && DETALLE_DEPENDE_DE_LA_OBRA.has(t.id)) {
      const raizDeEsta = rutaDe(destinoDe(t, enUrl) ?? t.ruta);
      if (rutaDe(url) !== raizDeEsta) return rutaDe(destinoDe(t, enUrl !== null ? obraId : null) ?? t.ruta);
    }
    const nueva = enUrl !== null || obraDeConsulta(url) !== null ? conObra(url, obraId) : url;
    return alCambiarDeObra(nueva);
  }

  /**
   * Antes de abrir una pantalla de UNA obra (guard de la zona protegida): la dirección manda. Si trae una obra distinta de la activa y el
   * usuario puede usar el módulo en ella, esa pasa a ser la activa (los correos y avisos traen enlaces así). Si no trae obra y la activa no
   * sirve para este módulo, se cambia sola a una que sí y se avisa.
   * Si la dirección trae una obra donde el usuario NO tiene este módulo (un enlace viejo o ajeno) pero sí lo tiene en otra, devuelve la misma
   * dirección con la obra que sí puede usar (y lo avisa): antes la pantalla se abría con la obra ajena y sus datos respondían 404. Si no hay nada que
   * corregir devuelve null.
   */
  async prepararObra(url: string): Promise<string | null> {
    const t = tarjetaDeRuta(url);
    if (!t || !usaObra(t)) return null;
    if (!this.obraActiva.cargado()) await this.obraActiva.cargar();
    const posibles = obrasDelModulo(t, this.obraActiva.obras(), this.permisos);
    if (posibles.length === 0) return null;
    const activa = this.obraActiva.obraActivaId();
    const enDireccion = obraDeRuta(url) ?? obraDeConsulta(url);
    try {
      if (enDireccion !== null) {
        if (posibles.some(o => o.id === enDireccion)) {
          if (enDireccion !== activa) await this.obraActiva.elegir(enDireccion);
          return null;
        }
        const destino = posibles.find(o => o.id === activa) ?? posibles[0];
        if (destino.id !== activa) await this.obraActiva.elegir(destino.id);
        this.avisos.mostrar(`No tienes acceso a esa obra en este módulo. Te llevamos a ${destino.codigo ? destino.codigo + ' — ' : ''}${destino.nombre}.`, 'alerta', 7000);
        return conObra(url, destino.id);
      }
      if (activa === null || !posibles.some(o => o.id === activa)) {
        const destino = posibles[0];
        await this.obraActiva.elegir(destino.id);
        if (activa !== null) {
          this.avisos.mostrar(`Cambiamos a ${destino.codigo ? destino.codigo + ' — ' : ''}${destino.nombre} porque tienes acceso a ella en este módulo.`);
        }
      }
    } catch { /* si el back no la acepta, la pantalla decide con la obra que ya había */ }
    return null;
  }
}

/** Guard de la zona protegida: deja la obra activa lista para la pantalla a la que se va (ver `ObraGlobalService.prepararObra`). Nunca bloquea: a lo más corrige la obra de la dirección. */
export const obraGuard: CanActivateChildFn = async (_ruta, state) => {
  // inject() solo vale antes del primer await: se toman las dos dependencias de una vez.
  const obras = inject(ObraGlobalService);
  const router = inject(Router);
  const otra = await obras.prepararObra(state.url);
  return otra ? router.parseUrl(otra) : true;
};
